import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import api from "../lib/api";
import LawyerSilhouette from "../components/LawyerSilhouette";
import { ArrowUpRight, Mail, Phone } from "lucide-react";
import { resolvePhotoUrl } from "../lib/photo";

export default function TeamPage() {
  const { t, lang } = useLang();
  const [team, setTeam] = useState([]);

  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="team-page" className="pt-40 lg:pt-44">
      {/* Header */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16">
        <div className="overline text-[#4A6A8F]">{t.team.eyebrow}</div>
        <h1 className="font-serif-ap text-5xl sm:text-6xl lg:text-7xl text-[#0F2746] mt-6 max-w-4xl leading-[1.02] tracking-tight">
          {t.team.title}
        </h1>
        <p className="text-[#3A5170] mt-8 max-w-2xl text-base lg:text-lg leading-relaxed">
          {lang === "tr"
            ? "Alaca Partners ekibi; her biri kendi alanında derin uzmanlığa sahip avukatlarımızdan oluşur. Aşağıdan bir profile tıklayarak özgeçmiş ve iletişim bilgilerine ulaşabilirsiniz."
            : "The Alaca Partners team is composed of lawyers, each with deep expertise in their own field. Click on a profile below to access biographies and contact information."}
        </p>
      </section>

      {/* Cards */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-24 lg:pb-28">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-14">
          {team.map((m, idx) => (
            <Link
              key={m.id}
              to={`/ekip/${m.id}`}
              data-testid={`team-member-${m.id}`}
              className="group block"
            >
              <div className="w-full aspect-[3/4] overflow-hidden bg-[#0F2746]/5 relative">
                {m.photo_url ? (
                  <img
                    src={resolvePhotoUrl(m.photo_url)}
                    alt={m.name}
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                  />
                ) : (
                  <LawyerSilhouette variant={idx} />
                )}
                <div className="absolute inset-0 bg-[#081A33]/0 group-hover:bg-[#081A33]/30 transition-colors duration-500 flex items-end p-4">
                  <div className="flex items-center gap-2 text-[#F3F5F8] opacity-0 group-hover:opacity-100 transition-opacity duration-500 overline text-[10px]">
                    {lang === "tr" ? "Profili Görüntüle" : "View Profile"} <ArrowUpRight size={12} />
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <div className="overline text-[10px] text-[#4A6A8F]">
                  {lang === "tr" ? m.title_tr : m.title_en}
                </div>
                <div className="font-serif-ap text-2xl text-[#0F2746] mt-2 leading-tight group-hover:text-[#C4A47C] transition-colors">
                  {m.name}
                </div>
                {(lang === "tr" ? m.bio_tr : m.bio_en) && (
                  <p className="text-sm text-[#3A5170] mt-3 leading-relaxed line-clamp-3">
                    {lang === "tr" ? m.bio_tr : m.bio_en}
                  </p>
                )}

                {/* Contact hints */}
                <div className="mt-5 space-y-1.5 text-xs text-[#0F2746]/70">
                  {m.email && (
                    <div className="flex items-center gap-2">
                      <Mail size={11} className="text-[#C4A47C]" />
                      <span className="truncate">{m.email}</span>
                    </div>
                  )}
                  {m.phone && (
                    <div className="flex items-center gap-2">
                      <Phone size={11} className="text-[#C4A47C]" />
                      <span>{m.phone}</span>
                    </div>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Bottom CTA — fills the page and invites contact */}
      <section className="bg-[#0F2746] text-[#F3F5F8] py-20 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="overline text-[#C4A47C]">
              {lang === "tr" ? "Birlikte çalışalım" : "Let's work together"}
            </div>
            <h2 className="font-serif-ap text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.05]">
              {lang === "tr"
                ? "Bir avukatımızla doğrudan görüşmek ister misiniz?"
                : "Would you like to speak directly with one of our lawyers?"}
            </h2>
            <p className="text-[#F3F5F8]/75 mt-5 max-w-xl">
              {lang === "tr"
                ? "Meselenizi anlatın; size en uygun ekip üyesini en kısa sürede yönlendirelim."
                : "Tell us about your matter and we'll connect you with the right team member promptly."}
            </p>
          </div>
          <div className="lg:col-span-4 lg:text-right">
            <Link
              to="/iletisim"
              data-testid="team-page-contact-cta"
              className="inline-flex items-center gap-3 bg-[#C4A47C] text-[#0F2746] hover:bg-[#F3F5F8] transition-all px-8 py-4 text-xs tracking-[0.2em] uppercase font-semibold"
            >
              {lang === "tr" ? "İletişime Geçin" : "Get in touch"} <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
